/**
 * React dependencies
 */

import React from 'react';

/**
 * DWT components
 */

import FeaturedFilter from './FilterItems/FeaturedFilter';
import FormatFilter from './FilterItems/FormatFilter';
import BrandFilter from './FilterItems/BrandFilter';
import ThemeFilter from './FilterItems/ThemeFilter';
import ChildrensCategoriesFilter from './FilterItems/ChildrensCategoriesFilter';
import LanguageFilter from './FilterItems/LanguageFilter';
import FilterPostItem from './FilterItems/FilterPostItem';
import AuthorLastInitialFilter from './FilterItems/AuthorLastInitialFilter';
import { FilterDataManager } from './FilterDataManager';
import { FILTER_COMPONENTS } from './FilterConfig';

/**
 * FilterOptions
 *
 * A component to render filters within an unordered list
 *
 * @param {Object} props
 * @param {Object} props.context
 * @param {string} props.currentPostType
 * @param {Object} props.setStart
 * @param {Object} props.scrollToTop
 */
const FilterOptions = ({ context, currentPostType, setStart, scrollToTop }) => {
  const contributorsListing = context.featureBy === 'contributors';
  const formats = context?.selectedFormats ? JSON.parse( context.selectedFormats ) : [];
  const languages = context?.selectedLanguages ? JSON.parse( context.selectedLanguages ) : [];
  const childrensCategories = context?.selectedChildrensCategories ? JSON.parse( context.selectedChildrensCategories ) : [];

  const dataManager = new FilterDataManager( [] );
  const filterData = context.filterData || {};

  // Get available filters for the current post type
  const availableFilters = dataManager.getAvailableFilters( currentPostType );

  const renderFilter = ( filterType, data = null ) => {
    const filterConfig = FILTER_COMPONENTS[filterType];
    if ( !filterConfig ) return null;

    const commonProps = {
      context,
      setStart,
      scrollToTop,
      contributorsListing
    };

    switch ( filterConfig.component ) {
    case 'FilterPostItem':
      return (
        <FilterPostItem
          key={filterType}
          {...commonProps}
          {...filterConfig.props}
          data={data || []}
        />
      );
    case 'BrandFilter':
      return (
        <BrandFilter
          key={filterType}
          {...commonProps}
          {...filterConfig.props}
          data={data || []}
        />
      );
    case 'ThemeFilter':
      return (
        <ThemeFilter
          key={filterType}
          {...commonProps}
          {...filterConfig.props}
          data={data || []}
        />
      );
    case 'FeaturedFilter':
      return <FeaturedFilter key={filterType} {...commonProps} {...filterConfig.props} />;
    case 'FormatFilter':
      return formats.length > 0 ? <FormatFilter key={filterType} {...commonProps} {...filterConfig.props} /> : null;
    case 'LanguageFilter':
      return languages.length > 0 ? <LanguageFilter key={filterType} {...commonProps} {...filterConfig.props} /> : null;
    case 'ChildrensCategoriesFilter':
      return childrensCategories.length > 0 ? <ChildrensCategoriesFilter key={filterType} {...commonProps} {...filterConfig.props} /> : null;
    case 'AuthorLastInitialFilter':
      return contributorsListing ? <AuthorLastInitialFilter key={filterType} {...commonProps} {...filterConfig.props} /> : null;
    default:
      return null;
    }
  };

  return (
    <div className='filters pt-20 pb-140 px-30 lg:p-0'>
      <h4 className='filters__list-heading m-0 text-14 text-[#606060] font-500 uppercase'>Filter By:</h4>
      <ul className='filters__list w-full list-none p-0'>
        {availableFilters.map( filterType => {
          switch( filterType ) {
          case 'age-range':
            return renderFilter( 'age-range', filterData.ageRange );
          case 'brand':
            return renderFilter( 'brand', filterData.brand );
          case 'category':
            return renderFilter( 'category', filterData.category );
          case 'childrens-categories':
            return renderFilter( 'childrens-categories' );
          case 'collection':
            return renderFilter( 'collection', filterData.collection );
          case 'featured':
            return renderFilter( 'featured' );
          case 'format':
            return renderFilter( 'format' );
          case 'imprint':
            return renderFilter( 'imprint', filterData.imprint );
          case 'language':
            return renderFilter( 'language' );
          case 'series':
            return renderFilter( 'series', filterData.series );
          case 'theme':
            return renderFilter( 'theme', filterData.theme );
          case 'author-last-initial':
            return contributorsListing ? renderFilter( 'author-last-initial' ) : null;
          default:
            return null;
          }
        })}
      </ul>
    </div>
  );
};

export default FilterOptions;
